<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Truncated List Component
      p.text--secondary A responsive list component that intelligently truncates items when they exceed available space, showing remaining items count with a tooltip.

    // Basic Examples
    .component-section.mb-8#basic-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-examples')") Basic Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in basicVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            .preview-container(:style="variation.containerStyle")
              yz-truncated-list(v-bind="variation.props" :items="variation.items")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Advanced Examples
    .component-section.mb-8#advanced-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('advanced-examples')") Advanced Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in advancedVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            .preview-container(:style="variation.containerStyle")
              yz-truncated-list(v-bind="variation.props" :items="variation.items")
                template(#visible-item="{ item }" v-if="variation.hasCustomSlot")
                  yz-badge(border :color="item.color" size="sm") {{ item.name }}
                template(#hidden-items="{ items }" v-if="variation.hasCustomSlot")
                  yz-badge(color="grey" size="sm" outlined) {{ `+${items.length} more` }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Interactive Demo
    .component-section.mb-8#interactive-demo
      h3.mb-4.cursor-pointer(@click="navigateToSection('interactive-demo')") Interactive Demo
      .variation-item
        .variation-header.mb-3
          h4 Live Demo
          p.text--secondary.caption Resize the container or modify properties to see how the component responds

        .demo-controls.mb-4.pa-4.border.rounded
          .row
            .col-12.col-md-4.mb-3
              label.text-sm.font-weight-medium Container Width
              v-slider(v-model="demoWidth" min="200" max="800" thumb-label)
            .col-12.col-md-4.mb-3
              label.text-sm.font-weight-medium Gap Size
              v-slider(v-model="demoGap" min="4" max="24" thumb-label)
            .col-12.col-md-4.mb-3
              label.text-sm.font-weight-medium Items Count
              v-slider(v-model="demoItemsCount" min="3" max="15" thumb-label)

        .mb-4
          .preview-container.demo-preview(:style="`max-width: ${demoWidth}px; border: 2px dashed #ccc; padding: 16px; background-color: #ffffff;`")
            yz-truncated-list(:key="demoKey" :items="demoItems" :gap="demoGap")

        .demo-info.pa-3.border.rounded(style="background-color: #f8f9fa;")
          .text-sm
            strong Current State:
            br
            | Container Width: {{ demoWidth }}px
            br
            | Gap: {{ demoGap }}px
            br
            | Total Items: {{ demoItemsCount }}
            br
            | Items Shown: Will be calculated dynamically based on available space

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3 Slots
      .slots-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Scope
                th Description
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.text--secondary.text-no-wrap {{ slot.scope }}
                td.single-line {{ slot.description }}

      h4.mb-3 Common Use Cases
      .use-cases-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Scenario
                th Recommended Approach
                th Example
            tbody
              tr(v-for="useCase in useCases" :key="useCase.scenario")
                td.font-weight-medium.text-no-wrap {{ useCase.scenario }}
                td.text--secondary {{ useCase.approach }}
                td.single-line {{ useCase.example }}

      h4.mb-3 Implementation Notes
      .notes-section
        v-alert(type="info" outlined)
          ul.mb-0
            li
              strong Responsive: 
              | Component automatically recalculates visible items on window resize
            li
              strong Performance: 
              | Uses efficient DOM measurement techniques to determine item visibility
            li
              strong Accessibility: 
              | Hidden items are accessible via tooltip with proper contrast and positioning
            li
              strong Flexibility: 
              | Supports custom slots for both visible and hidden item rendering
            li
              strong Gap Control: 
              | Customizable spacing between items with pixel-perfect calculation
</template>

<script>
export default {
  name: "TruncatedList",
  data() {
    return {
      copiedCode: null,
      demoWidth: 400,
      demoGap: 12,
      demoItemsCount: 8,
      demoKey: 1
    }
  },
  computed: {
    basicVariations() {
      return [
        {
          name: 'Simple String List',
          id: 'simple-string-list',
          description: 'Basic truncated list with string items',
          label: 'String Items',
          hint: 'Resize window to see truncation',
          containerStyle: 'width: 300px; border: 2px dashed #ccc; padding: 16px;',
          items: ['Product A', 'Product B', 'Product C', 'Product D', 'Product E', 'Product F'],
          props: {},
          code: `yz-truncated-list(:items="['Product A', 'Product B', 'Product C', 'Product D', 'Product E', 'Product F']")`
        },
        {
          name: 'Object List with textItem',
          id: 'object-list',
          description: 'List with object items using textItem prop',
          label: 'Object Items',
          hint: 'Objects with name property',
          containerStyle: 'width: 400px; border: 2px dashed #ccc; padding: 16px;',
          items: [
            { id: 1, name: 'Electronics' },
            { id: 2, name: 'Clothing' },
            { id: 3, name: 'Home & Garden' },
            { id: 4, name: 'Sports & Outdoors' },
            { id: 5, name: 'Books' }
          ],
          props: {
            textItem: 'name'
          },
          code: `yz-truncated-list(
  :items="categories"
  text-item="name"
)`
        },
        {
          name: 'Custom Gap',
          id: 'custom-gap',
          description: 'List with custom spacing between items',
          label: 'Custom Gap (24px)',
          hint: 'Larger spacing between items',
          containerStyle: 'width: 350px; border: 2px dashed #ccc; padding: 16px;',
          items: ['Tag 1', 'Tag 2', 'Tag 3', 'Tag 4', 'Tag 5'],
          props: {
            gap: 24
          },
          code: `yz-truncated-list(
  :items="tags"
  :gap="24"
)`
        },
        {
          name: 'Small Container',
          id: 'small-container',
          description: 'List in a very constrained space',
          label: 'Constrained Width',
          hint: 'Only 200px wide',
          containerStyle: 'width: 200px; border: 2px dashed #ccc; padding: 16px;',
          items: ['Long Category Name', 'Another Long Name', 'Short', 'Medium Length', 'Very Long Category Name'],
          props: {},
          code: `// Container with limited width
.constrained-container {
  width: 200px;
}

yz-truncated-list(:items="longNames")`
        }
      ]
    },
    advancedVariations() {
      return [
        {
          name: 'Custom Visible Item Slot',
          id: 'custom-visible-slot',
          description: 'Custom rendering for visible items using slots',
          label: 'Custom Chip Style',
          hint: 'Colored chips with custom styling',
          containerStyle: 'width: 300px; border: 2px dashed #ccc; padding: 16px;',
          items: [
            { name: 'Critical', color: 'error' },
            { name: 'Warning', color: 'warning' },
            { name: 'Success', color: 'success' },
            { name: 'Info', color: 'info' },
            { name: 'Default', color: 'primary' }
          ],
          props: {},
          hasCustomSlot: true,
          code: `yz-truncated-list(:items="statusItems")
  template(#visible-item="{ item }")
    yz-chip(:label="item.name" :color="item.color" size="sm")
  template(#hidden-items="{ items }")
    yz-chip(:label="\`+\${items.length} more\`" color="grey" size="sm" outlined)`
        },
        {
          name: 'With Offset',
          id: 'with-offset',
          description: 'List with offset to reserve space for additional content',
          label: 'Reserved Space',
          hint: 'Extra space reserved on the right',
          containerStyle: 'width: 350px; border: 2px dashed #ccc; padding: 16px; position: relative;',
          items: ['Apple', 'Banana', 'Cherry', 'Date', 'Elderberry', 'Fig'],
          props: {
            offset: 80
          },
          code: `yz-truncated-list(
  :items="fruits"
  :offset="80"
)
// Reserve 80px for additional content`
        }
      ]
    },
    demoItems() {
      const baseItems = ['Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta', 'Eta', 'Theta', 'Iota', 'Kappa', 'Lambda', 'Mu', 'Nu', 'Xi', 'Omicron']
      return baseItems.slice(0, this.demoItemsCount)
    },
    componentProps() {
      return [
        {
          name: 'items',
          type: 'Array',
          default: 'required',
          description: 'Array of items to display. Can be strings or objects'
        },
        {
          name: 'textItem',
          type: 'String',
          default: 'null',
          description: 'Property name to use for text when items are objects'
        },
        {
          name: 'gap',
          type: 'Number',
          default: '12',
          description: 'Gap in pixels between items'
        },
        {
          name: 'offset',
          type: 'Number',
          default: '0',
          description: 'Additional space to reserve (in pixels) when calculating visible items'
        }
      ]
    },
    componentSlots() {
      return [
        {
          name: 'visible-item',
          scope: '{ item, index }',
          description: 'Custom template for each visible item. Receives the item and its index'
        },
        {
          name: 'hidden-items',
          scope: '{ items }',
          description: 'Custom template for the truncation indicator. Receives array of hidden items'
        },
        {
          name: 'hidden-item-tooltip',
          scope: '{ item }',
          description: 'Custom template for each item in the tooltip. Receives individual hidden item'
        }
      ]
    },
    useCases() {
      return [
        {
          scenario: 'Tag lists',
          approach: 'Use with string array for simple tag display',
          example: 'yz-truncated-list(:items="tags")'
        },
        {
          scenario: 'Category filters',
          approach: 'Use with objects and textItem prop',
          example: 'yz-truncated-list(:items="categories" text-item="name")'
        },
        {
          scenario: 'Status indicators',
          approach: 'Use custom slots for colored chips or badges',
          example: 'Custom visible-item slot with yz-chip components'
        },
        {
          scenario: 'Responsive layouts',
          approach: 'Component automatically adapts to container width changes',
          example: 'Works with flex layouts and CSS Grid'
        },
        {
          scenario: 'Space-constrained UI',
          approach: 'Use offset prop to reserve space for buttons or icons',
          example: 'yz-truncated-list(:items="items" :offset="60")'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    },
    demoWidth() {
      this.demoKey++
    },
    demoGap() {
      this.demoKey++
    },
    demoItemsCount() {
      this.demoKey++
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #ffffff;
  border: 1px dashed #d0d0d0 !important;
}

.preview-container {
  min-height: 60px;
  display: flex;
  align-items: center;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.demo-controls {
  background-color: #f8f9fa;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.demo-preview {
  width: 100%;
}

@media (max-width: 600px) {
  .demo-preview {
    max-width: 100% !important;
    width: 100%;
  }
}
</style>